import React, { useMemo, useState } from "react";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LOAN_PRODUCTS, inr } from "@/lib/api";

const DEFAULT_RATES = {
  personal_loan: 14.0, business_loan: 13.5, home_loan: 8.75,
  mortgage_loan: 10.5, loan_against_property: 10.75, new_car_loan: 9.25,
  used_car_loan: 12.5, commercial_vehicle_loan: 11.5, msme_loan: 12.0,
};

function calcEMI(P, R, N) {
  const r = R / 12 / 100;
  if (r === 0) return P / N;
  const emi = (P * r * Math.pow(1 + r, N)) / (Math.pow(1 + r, N) - 1);
  return Math.round(emi);
}

export default function EMICalculator() {
  const [product, setProduct] = useState("home_loan");
  const [amount, setAmount] = useState(2500000);
  const [rate, setRate] = useState(DEFAULT_RATES.home_loan);
  const [years, setYears] = useState(15);

  const months = years * 12;
  const emi = useMemo(() => calcEMI(amount, rate, months), [amount, rate, months]);
  const total = emi * months;
  const interest = total - amount;

  const onProductChange = (key) => {
    setProduct(key);
    setRate(DEFAULT_RATES[key] || 12);
  };

  return (
    <section className="py-16 bg-[#F4F4F5] min-h-[80vh]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-xs font-semibold tracking-[0.2em] uppercase text-[#0052FF]">EMI Calculator</div>
        <h1 className="mt-2 font-display text-4xl sm:text-5xl font-bold tracking-tight text-[#06152D]">
          Estimate your monthly EMI
        </h1>
        <p className="mt-3 text-[#52525B] max-w-2xl">
          Indicative only. Final EMI, rate and processing fee are decided by the sanctioning Bank/NBFC.
        </p>

        <div className="mt-10 grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 bg-white border border-[#E4E4E7] rounded-2xl p-8">
            <div className="mb-6">
              <Label className="text-sm font-semibold text-[#06152D]">Loan product</Label>
              <div className="mt-2 flex flex-wrap gap-2">
                {LOAN_PRODUCTS.map((p) => (
                  <button
                    key={p.key}
                    onClick={() => onProductChange(p.key)}
                    className={`text-xs font-medium px-3 py-1.5 rounded-full border transition-colors ${
                      product === p.key
                        ? "bg-[#0052FF] border-[#0052FF] text-white"
                        : "bg-white border-[#E4E4E7] text-[#52525B] hover:border-[#0052FF]"
                    }`}
                    data-testid={`emi-product-${p.key}`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-8">
              <div>
                <div className="flex justify-between items-baseline">
                  <Label className="text-sm font-semibold text-[#06152D]">Loan amount</Label>
                  <div className="font-mono-num font-bold text-[#0052FF]">{inr(amount)}</div>
                </div>
                <Slider min={50000} max={20000000} step={50000} value={[amount]}
                        onValueChange={([v]) => setAmount(v)} className="mt-3" data-testid="emi-amount-slider" />
                <Input type="number" value={amount} onChange={(e) => setAmount(+e.target.value || 0)}
                       className="mt-3 max-w-[220px] font-mono-num" data-testid="emi-amount-input" />
              </div>

              <div>
                <div className="flex justify-between items-baseline">
                  <Label className="text-sm font-semibold text-[#06152D]">Interest rate (annual)</Label>
                  <div className="font-mono-num font-bold text-[#0052FF]">{rate.toFixed(2)}%</div>
                </div>
                <Slider min={6} max={24} step={0.25} value={[rate]}
                        onValueChange={([v]) => setRate(v)} className="mt-3" data-testid="emi-rate-slider" />
              </div>

              <div>
                <div className="flex justify-between items-baseline">
                  <Label className="text-sm font-semibold text-[#06152D]">Tenure (years)</Label>
                  <div className="font-mono-num font-bold text-[#0052FF]">{years} years</div>
                </div>
                <Slider min={1} max={30} step={1} value={[years]}
                        onValueChange={([v]) => setYears(v)} className="mt-3" data-testid="emi-tenure-slider" />
              </div>
            </div>
          </div>

          <aside className="bg-[#06152D] text-white rounded-2xl p-8 lj-grain h-fit">
            <div className="text-xs font-semibold tracking-[0.2em] uppercase text-[#8AB4FF]">Your monthly EMI</div>
            <div className="mt-2 font-display font-extrabold text-4xl" data-testid="emi-result">{inr(emi)}</div>
            <div className="mt-6 space-y-4 pt-6 border-t border-white/10 text-sm">
              <div className="flex justify-between"><span className="text-[#B5C4E1]">Principal</span><span className="font-mono-num font-semibold">{inr(amount)}</span></div>
              <div className="flex justify-between"><span className="text-[#B5C4E1]">Total interest</span><span className="font-mono-num font-semibold text-[#F59E0B]">{inr(interest)}</span></div>
              <div className="flex justify-between"><span className="text-[#B5C4E1]">Total payment</span><span className="font-mono-num font-semibold">{inr(total)}</span></div>
              <div className="flex justify-between"><span className="text-[#B5C4E1]">Tenure</span><span className="font-mono-num">{months} months</span></div>
            </div>
            <a href={`/apply?loan=${product}`} className="mt-6 block text-center bg-[#0052FF] hover:bg-[#003FCC] text-white font-semibold px-4 py-3 rounded-full" data-testid="emi-apply-cta">
              Apply for this loan
            </a>
          </aside>
        </div>
      </div>
    </section>
  );
}
